import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, money } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { shortOrderId } from "../../lib/format";

const LABEL = {
  pending: { t: "Pendiente", c: "bg-neutral-100 text-neutral-700" },
  confirmed: { t: "Confirmado", c: "bg-blue-50 text-blue-700" },
  on_the_way: { t: "En camino", c: "bg-orange-50 text-orange-700" },
  delivered: { t: "Entregado", c: "bg-green-50 text-green-700" },
  cancelled: { t: "Cancelado", c: "bg-red-50 text-red-700" },
};

const PAGE_SIZE = 10;

export default function Orders() {
  const [orders, setOrders] = useState([]);
  // Paginado client-side: 10 por página. Si en el futuro la cantidad
  // de pedidos crece mucho conviene mover a paginación server-side.
  const [page, setPage] = useState(1);
  useEffect(() => {
    api.get("/orders/mine").then((r) => setOrders(r.data));
  }, []);

  const totalPages = Math.max(1, Math.ceil(orders.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return orders.slice(start, start + PAGE_SIZE);
  }, [orders, safePage]);

  return (
    <div className="min-h-screen bg-neutral-50">
      <main className="max-w-5xl mx-auto px-4 md:px-6 py-6">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Mis pedidos</h1>
          {orders.length > 0 && (
            <p className="text-xs text-neutral-500" data-testid="orders-count">
              {orders.length} {orders.length === 1 ? "pedido" : "pedidos"}
            </p>
          )}
        </div>
        <div className="mt-6 space-y-3">
          {orders.length === 0 ? (
            <p className="text-neutral-500 text-sm">Aún no tienes pedidos.</p>
          ) : (
            pageItems.map((o) => {
              const l = LABEL[o.status] || LABEL.pending;
              return (
                <Link to={`/app/orders/${o.order_id}`} key={o.order_id} className="block rounded-xl bg-white border border-neutral-200 p-4 hover:border-black transition-colors" data-testid={`order-row-${o.order_id}`}>
                  <div className="flex justify-between items-start gap-4">
                    <div>
                      <p className="text-xs text-neutral-400 font-mono">#{shortOrderId(o.order_id)}</p>
                      <p className="font-semibold mt-0.5">{o.items.map((i) => `${i.name} × ${i.quantity}`).join(", ")}</p>
                      <p className="text-sm text-neutral-500 mt-1">{new Date(o.created_at).toLocaleString("es")}</p>
                    </div>
                    <div className="text-right">
                      <span className={`text-xs font-semibold rounded-full px-2 py-1 ${l.c}`}>{l.t}</span>
                      <p className="font-display font-extrabold text-lg mt-1">{money(o.total)}</p>
                    </div>
                  </div>
                </Link>
              );
            })
          )}
        </div>

        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between gap-3" data-testid="orders-pagination">
            <Button
              variant="outline"
              size="sm"
              disabled={safePage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="h-10 tap-fade"
              data-testid="orders-page-prev"
            >
              <ChevronLeft size={14} className="mr-1" /> Anterior
            </Button>
            <p className="text-sm text-neutral-600 tabular-nums">
              Página <span className="font-semibold">{safePage}</span> de <span className="font-semibold">{totalPages}</span>
            </p>
            <Button
              variant="outline"
              size="sm"
              disabled={safePage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="h-10 tap-fade"
              data-testid="orders-page-next"
            >
              Siguiente <ChevronRight size={14} className="ml-1" />
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
